import {
  AudioDeviceInfo,
  Call,
  CallAgent,
  CallClient,
  DeviceAccess,
  DeviceManager,
  LocalVideoStream,
  VideoDeviceInfo,
  VideoStreamRenderer,
  VideoStreamRendererView,
} from "@azure/communication-calling";
import { AzureCommunicationTokenCredential } from "@azure/communication-common";
import { logger } from "@mwt/web-js-telemetry";

import { ACS_TOKEN_RETRY } from "../../../shared/apiConstants";
import { ACS_TEST_CALL_ID } from "../../../environments/environment";
import {
  DeviceInfo,
  NullableAudioDevice,
  NullableAudioDeviceList,
  NullableVideoDevice,
  NullableVideoDeviceList,
} from "../../../types/Device.types";

import { isApiResponseError, isACSToken } from "./utils/apiResponseTypeGuard";
import { AcsToken, AcsTokenError } from "./acsToken.types";
import { IACSService, IAPIService } from "./Service.types";

export class ACSService implements IACSService {
  public deviceManager: DeviceManager | undefined;
  private readonly testCallId = ACS_TEST_CALL_ID;
  private readonly apiService: IAPIService;
  private callClient: CallClient | undefined;
  private callAgent: CallAgent | undefined;
  private tokenCredential: AzureCommunicationTokenCredential | undefined;
  private tenantId: string | undefined;

  constructor(apiService: IAPIService, tid: string) {
    this.apiService = apiService;
    this.tenantId = tid;
  }

  public async refreshAudioDevices(
    deviceManager: DeviceManager,
    updateAudioDevices: (
      newMicrophoneDevices: NullableAudioDeviceList,
      newSpeakerDevices: NullableAudioDeviceList
    ) => void
  ): Promise<void> {
    let newMicrophoneDevices: AudioDeviceInfo[] = [];
    let newSpeakerDevices: AudioDeviceInfo[] = [];
    try {
      newMicrophoneDevices = await deviceManager.getMicrophones();
      newSpeakerDevices = await deviceManager.getSpeakers();

      // convert from AudioDeviceInfo to NullableAudioDevice
      const newMicrophoneDevicesList: NullableAudioDeviceList =
        newMicrophoneDevices?.map((device) => {
          return {
            id: device.id,
            name: device.name,
            isSystemDefault: device.isSystemDefault,
            deviceType: device.deviceType,
          };
        });
      const newSpeakerDevicesList: NullableAudioDeviceList =
        newSpeakerDevices?.map((device) => {
          return {
            id: device.id,
            name: device.name,
            isSystemDefault: device.isSystemDefault,
            deviceType: device.deviceType,
          };
        });
      updateAudioDevices(newMicrophoneDevicesList, newSpeakerDevicesList);
      return;
    } catch (error) {
      logger.error("error refreshing audio devices");
      return;
    }
  }

  public async refreshVideoDevices(
    deviceManager: DeviceManager,
    updateVideoDevices: (newCameraDevices: NullableVideoDeviceList) => void
  ): Promise<void> {
    let newCameraDevices: VideoDeviceInfo[] = [];
    try {
      newCameraDevices = await deviceManager.getCameras();
      // convert from VideoDeviceInfo to NullableVideoDevice
      const newVideoDevicesList: NullableVideoDeviceList =
        newCameraDevices?.map((device) => {
          return {
            id: device.id,
            name: device.name,
            deviceType: device.deviceType,
          };
        });
      updateVideoDevices(newVideoDevicesList);
      return;
    } catch (error) {
      // todo add logger
      logger.error("error refreshing video devices");
      return;
    }
  }

  // Subscribe to changes to Camera, Mic, and Speaker device lists
  public async manageDeviceUpdateSubscription(
    updateVideoDevices: (newCameraDevices: NullableVideoDeviceList) => void,
    updateAudioDevices: (
      newMicrophoneDevices: NullableAudioDeviceList,
      newSpeakerDevices: NullableAudioDeviceList
    ) => void,
    unsubscribe: boolean
  ): Promise<void> {
    // if deviceManager is not defined, get it
    if (!this.deviceManager) {
      await this.getDeviceManager();
    }

    // if deviceManager is still not defined, return
    if (!this.deviceManager) {
      return;
    }

    // if unsubscribe is false, subscribe to device updates
    if (!unsubscribe) {
      this.deviceManager.on("videoDevicesUpdated", async () => {
        await this.refreshVideoDevices(
          this.deviceManager as DeviceManager,
          updateVideoDevices
        );
      });

      this.deviceManager.on("audioDevicesUpdated", async () => {
        await this.refreshAudioDevices(
          this.deviceManager as DeviceManager,
          updateAudioDevices
        );
      });
    }

    // if unsubscribe is true, unsubscribe from device updates
    else {
      this.deviceManager.off("videoDevicesUpdated", async () => {
        await this.refreshAudioDevices(
          this.deviceManager as DeviceManager,
          updateAudioDevices
        );
      });
      this.deviceManager.off("audioDevicesUpdated", async () => {
        await this.refreshAudioDevices(
          this.deviceManager as DeviceManager,
          updateAudioDevices
        );
      });
    }
  }

  // Get permissions for audio and camera access from user
  public async getDeviceManager(): Promise<DeviceAccess | null> {
    const callClient = new CallClient();
    const deviceManager = await callClient.getDeviceManager();
    if (deviceManager) {
      const permission = await deviceManager.askDevicePermission({
        audio: true,
        video: true,
      });
      return permission;
    }
    return null;
  }

  // Get permissions for audio and camera access from user
  public async checkDevicePermission(): Promise<DeviceAccess | null> {
    const callClient = new CallClient();
    const deviceManager = await callClient.getDeviceManager();
    if (deviceManager) {
      const permission = await deviceManager.askDevicePermission({
        audio: true,
        video: true,
      });
      return permission;
    }
    return null;
  }

  public async preFetchACSToken(): Promise<boolean> {
    // Only try to fetch an ACS token if a Tenant ID is present
    if (!this.tenantId) {
      logger.error("Can't fetch ACS Token, the TenantId is not set.");
      return false;
    }
    // Fetch the acs token (3x retry) and configure the token credential
    if (!this.tokenCredential) {
      for (let i = 0; i < ACS_TOKEN_RETRY; i++) {
        const tokenResponse: AcsToken | AcsTokenError =
          await this.apiService.getACSToken();

        //Check if the token response was an error
        if (isApiResponseError(tokenResponse)) {
          // Log the api error message and status code
          logger.error(
            `error in ACS token fetching. Error msg: ${tokenResponse?.message}, status code: ${tokenResponse?.status}`
          );
        }

        if (isACSToken(tokenResponse)) {
          this.tokenCredential = new AzureCommunicationTokenCredential(
            tokenResponse.token
          );

          logger.info(
            `ACS token received from Region: ${tokenResponse.region} from acsEndpoint: ${tokenResponse.acsEndpoint} `
          );

          return true;
        }
      }
    }
    // If we couldn't fetch token after 3 attempts
    return false;
  }

  // Create a new call client with device manager and call agent
  public async createCallClient(): Promise<void> {
    /*
    Need to create a new CallClient after permissions have been received
    to enable us to obtain access to camera and audio device lists
    */
    this.callClient = new CallClient();
    this.deviceManager = await this.callClient.getDeviceManager();

    if (this.tokenCredential) {
      this.callAgent = await this.callClient?.createCallAgent(
        this.tokenCredential
      );
    }
  }

  // Get device settings for audio and camera and prefetch ACS token
  public async getDeviceInfo(): Promise<DeviceInfo | null> {
    // 1 - Ask for device permission
    const deviceCheckPermissions = await this.checkDevicePermission();
    // 2 - Prefetch ACS token and configure token credential if not already created
    const tokenFetchSuccess = await this.preFetchACSToken();
    // 3 - Configure the call client, device manager, and call agent
    await this.createCallClient();

    // 4 - Get the speaker/mic/cam device info
    if (this.deviceManager) {
      let microphones: NullableAudioDeviceList;
      let speakers: NullableAudioDeviceList;
      let cameras: NullableVideoDeviceList;
      let defaultMicrophone: NullableAudioDevice;
      let defaultSpeaker: NullableAudioDevice;
      let defaultCamera: NullableVideoDevice;
      let micAccess = false;
      let cameraAccess = false;

      if (deviceCheckPermissions?.audio) {
        micAccess = true;
        // If audio permissions supplied enumerate microphones and set default
        // microphone
        microphones = await this.deviceManager.getMicrophones();
        defaultMicrophone = this.deviceManager.selectedMicrophone;

        // Check if speaker selection is possible on current device, some
        // OS types such as iOS do not support speaker enumeration
        if (this.deviceManager?.isSpeakerSelectionAvailable) {
          speakers = await this.deviceManager.getSpeakers();
          defaultSpeaker = this.deviceManager.selectedSpeaker;
        }
      }
      if (deviceCheckPermissions?.video) {
        cameraAccess = true;
        // Enumerate potential camera devices to use
        cameras = await this.deviceManager.getCameras();
        defaultCamera = cameras?.[0];
      }
      return {
        microphones: microphones,
        speakers: speakers,
        cameras: cameras,
        defaultMicrophone: defaultMicrophone,
        defaultSpeaker: defaultSpeaker,
        defaultCamera: defaultCamera,
        micAccess: micAccess,
        cameraAccess: cameraAccess,
        isSpeakerSelectionAvailable:
          this.deviceManager.isSpeakerSelectionAvailable,
        isAcsTokenFetched: tokenFetchSuccess,
      };
    }
    return null;
  }

  public async preconfigureTestCall(
    selectedMicrophone: AudioDeviceInfo | null | undefined,
    selectedSpeaker: AudioDeviceInfo | null | undefined
  ) {
    if (this.deviceManager) {
      //Set selected speaker if it's set
      if (this.deviceManager.isSpeakerSelectionAvailable && selectedSpeaker) {
        await this.deviceManager.selectSpeaker(selectedSpeaker);
      }
      //Set selected mic if it's set
      if (selectedMicrophone) {
        await this.deviceManager.selectMicrophone(selectedMicrophone);
      }
    } else {
      logger.error("failure creating token credential");
    }
  }

  //Initiate the ACS Test Call
  public async startTestCall(): Promise<Call | undefined> {
    // Try to create token credential if doesn't exist
    if (!this.tokenCredential) {
      await this.preFetchACSToken();
    }
    // If call agent was not configured then re-create it
    if (!this.callAgent) {
      await this.createCallClient();
    }

    if (this.callAgent && this.testCallId) {
      // Start Echo bot test Call
      return this.callAgent.startCall([{ id: this.testCallId }], {});
    }
  }

  //Get the camera streaming view
  public async setCameraStreaming(
    selectedCamera: VideoDeviceInfo
  ): Promise<VideoStreamRendererView | null> {
    const localVideoStream = new LocalVideoStream(selectedCamera);
    const renderer: VideoStreamRenderer = new VideoStreamRenderer(
      localVideoStream
    );
    const rendererCreateView = await renderer.createView({
      scalingMode: "Crop",
      isMirrored: true,
    });
    return rendererCreateView;
  }
}
